/**
 * Interactive Quiz Widget
 * 
 * Usage:
 * 1. Add a div: <div id="quiz-container"></div>
 * 2. Include this script: <script src="quiz-widget.js"></script>
 * 3. Initialize: QuizWidget.init('quiz-container', 'quiz-questions.json');
 * 
 * CSS is automatically loaded from quiz-widget.css in the same directory
 */

(function (window) {
    'use strict';

    const QuizWidget = {
        // Configuration
        config: {
            containerId: null,
            questionsUrl: null,
            quizFolder: null,
            signupEmbed: '',
            hiddenFieldName: 'quiz_result'
        },

        // State
        state: {
            quizData: [],
            currentQuestion: 0,
            userAnswers: [],
            selectedAnswer: null
        },

        // Initialize the quiz
        init: function (containerId, questionsUrl) {
            this.config.containerId = containerId;
            this.config.questionsUrl = questionsUrl;

            // Determine executing script folder
            this.getPath();

            // Load CSS first
            this.loadCSS();

            // Then load questions
            this.loadQuestions();
        },

        // Determine widget folder
        getPath: function(){
            const script = document.getElementById('quiz-widget-script');
            if (script && script.src) {
                // Strip off /quiz-widget.js
                this.config.quizFolder = script.src.substring(0, script.src.lastIndexOf('/') + 1);
            }
        },


        // Dynamically load CSS
        loadCSS: function () {
            // Check if CSS is already loaded
            if (document.getElementById('quiz-widget-css')) {
                return;
            }

            // Create and inject CSS link
            const link = document.createElement('link');
            link.id = 'quiz-widget-css';
            link.rel = 'stylesheet';
            link.type = 'text/css';
            link.href = this.config.quizFolder + 'quiz-widget.css';
            document.head.appendChild(link);
        },

        // Load questions from JSON file
        loadQuestions: function () {
            const self = this;
            fetch(this.config.quizFolder + this.config.questionsUrl)
                .then(response => {
                    if (!response.ok) {
                        throw new Error('Failed to load quiz questions');
                    }
                    return response.json();
                })
                .then(data => {
                    self.config.signupEmbed = data.config.signupEmbed || '';
                    self.config.hiddenFieldName = data.config.hiddenFieldName || 'quiz_result';
                    self.state.quizData = data.questions;

                    self.renderWidget();
                    self.displayQuestion();
                    self.updateProgress();
                    self.updateNavigationButtons();
                })
                .catch(error => {
                    console.error('Quiz Widget Error:', error);
                    const container = document.getElementById(self.config.containerId);
                    if (container) {
                        container.innerHTML = '<p style="color: red;">Failed to load quiz. Please try again later.</p>';
                    }
                });
        },

        // Render the widget HTML
        renderWidget: function () {
            const container = document.getElementById(this.config.containerId);
            if (!container) {
                console.error('Quiz container not found:', this.config.containerId);
                return;
            }

            // Create HTML structure
            container.innerHTML = `
                <div class="quiz-widget">
                    <div id="quizSection" class="quiz-section">
                        <h1 class="quiz-title">Find your dominant element.</h1>
                        <div class="quiz-progress-bar">
                            <div class="quiz-progress-fill" id="progressFill"></div>
                        </div>
                        <p id="questionCounter" class="quiz-counter"></p>
                        
                        <div class="quiz-question-container">
                            <h2 class="quiz-question-text" id="questionText"></h2>
                            <div id="answersContainer"></div>
                        </div>

                        <div class="quiz-navigation-buttons">
                            <button class="quiz-btn quiz-btn-secondary" id="prevBtn">Previous</button>
                            <button class="quiz-btn quiz-btn-primary" id="nextBtn" disabled>Next</button>
                        </div>
                    </div>

                    <div id="signupSection" class="quiz-signup-section">
                        <div class="quiz-result-summary">
                            <h2>Your Result</h2>
                            <p class="quiz-result-value" id="resultValue"></p>
                        </div>
                        
                        <h2 class="quiz-signup-title">Email me my downloadable Element Guide.</h2>
                        
                        <div id="signupFormContainer" class="quiz-form-embed"></div>
                    </div>
                </div>
            `;

            // Attach event listeners
            this.attachEventListeners();
        },

        // Attach event listeners
        attachEventListeners: function () {
            const self = this;

            document.getElementById('prevBtn').addEventListener('click', function () {
                self.previousQuestion();
            });

            document.getElementById('nextBtn').addEventListener('click', function () {
                self.nextQuestion();
            });
        },

        // Display current question
        displayQuestion: function () {
            const question = this.state.quizData[this.state.currentQuestion];
            document.getElementById('questionText').textContent = question.question;
            document.getElementById('questionCounter').textContent =
                `Question ${this.state.currentQuestion + 1} of ${this.state.quizData.length}`;

            const answersContainer = document.getElementById('answersContainer');
            answersContainer.innerHTML = '';

            const self = this;
            question.answers.forEach(function (answer, index) {
                const button = document.createElement('button');
                button.className = 'quiz-answer-option';
                button.textContent = answer.text;
                button.addEventListener('click', function () {
                    self.selectAnswer(index);
                });

                // Restore previous selection if exists
                if (self.state.userAnswers[self.state.currentQuestion] === index) {
                    button.classList.add('selected');
                    self.state.selectedAnswer = index;
                }

                answersContainer.appendChild(button);
            });
        },

        // Handle answer selection
        selectAnswer: function (index) {
            this.state.selectedAnswer = index;

            // Update UI
            const buttons = document.querySelectorAll('.quiz-answer-option');
            buttons.forEach(function (btn) {
                btn.classList.remove('selected');
            });
            buttons[index].classList.add('selected');

            // Enable next button
            document.getElementById('nextBtn').disabled = false;
        },

        // Navigate to next question
        nextQuestion: function () {
            if (this.state.selectedAnswer === null) return;

            // Save answer
            this.state.userAnswers[this.state.currentQuestion] = this.state.selectedAnswer;

            if (this.state.currentQuestion < this.state.quizData.length - 1) {
                this.state.currentQuestion++;
                this.state.selectedAnswer = this.state.userAnswers[this.state.currentQuestion] !== undefined ?
                    this.state.userAnswers[this.state.currentQuestion] : null;
                this.displayQuestion();
                this.updateProgress();
                this.updateNavigationButtons();
            } else {
                // Quiz complete
                this.showSignupForm();
            }
        },

        // Navigate to previous question
        previousQuestion: function () {
            if (this.state.currentQuestion > 0) {
                this.state.currentQuestion--;
                this.state.selectedAnswer = this.state.userAnswers[this.state.currentQuestion] !== undefined ?
                    this.state.userAnswers[this.state.currentQuestion] : null;
                this.displayQuestion();
                this.updateProgress();
                this.updateNavigationButtons();
            }
        },

        // Update progress bar
        updateProgress: function () {
            const progress = ((this.state.currentQuestion + 1) / this.state.quizData.length) * 100;
            document.getElementById('progressFill').style.width = progress + '%';
        },

        // Update navigation buttons
        updateNavigationButtons: function () {
            const prevBtn = document.getElementById('prevBtn');
            const nextBtn = document.getElementById('nextBtn');

            prevBtn.style.display = this.state.currentQuestion === 0 ? 'none' : 'block';
            nextBtn.textContent = this.state.currentQuestion === this.state.quizData.length - 1 ? 'Finish' : 'Next';
            nextBtn.disabled = this.state.selectedAnswer === null;
        },

        // Calculate most common value
        calculateResult: function () {
            const valueCounts = {};
            const self = this;

            this.state.userAnswers.forEach(function (answerIndex, questionIndex) {
                const value = self.state.quizData[questionIndex].answers[answerIndex].value;
                valueCounts[value] = (valueCounts[value] || 0) + 1;
            });

            // Find most common value
            let maxCount = 0;
            let result = '';

            for (const value in valueCounts) {
                if (valueCounts.hasOwnProperty(value)) {
                    if (valueCounts[value] > maxCount) {
                        maxCount = valueCounts[value];
                        result = value;
                    }
                }
            }

            return result;
        },

        // Show signup form
        showSignupForm: function () {
            const result = this.calculateResult();

            document.getElementById('quizSection').classList.add('quiz-hidden');
            document.getElementById('signupSection').classList.add('active');
            document.getElementById('resultValue').textContent = result;

            // Inject the signup form embed and execute any scripts
            const signupContainer = document.getElementById('signupFormContainer');
            this.injectHTML(signupContainer, this.config.signupEmbed);

            // Set up Klaviyo form submit listener
            this.setupKlaviyoListener(result);

            /* 
            SKIP FOR NOW
            // As backup for non-Klaviyo forms, wait a moment for the embed to load, then try to fill the hidden field
            const self = this;
            setTimeout(function () {
                self.fillHiddenField(result);
            }, 500);

            // And backup for the backup, also set up a mutation observer to catch dynamically added forms
            this.observeFormInjection(result); 
            */
        },

        // Setup Klaviyo form submit event listener
        setupKlaviyoListener: function (result) {
            const self = this;

            window.addEventListener('klaviyoForms', function (e) {
                if (e.detail.type == 'embedOpen') {
                    console.log('Klaviyo form opened');
                }
                if (e.detail.type === 'submit') {
                    var klaviyo = window.klaviyo || [];

                    // Create the identify object with the dynamic field name
                    var identifyData = {};
                    identifyData[self.config.hiddenFieldName] = result;

                    klaviyo.push(['identify', identifyData]);

                    console.log('Klaviyo form submitted with quiz result:', result);
                }
            });
        },

        // Inject HTML and execute any scripts within it
        injectHTML: function (container, html) {
            // Create a temporary container
            const temp = document.createElement('div');
            temp.innerHTML = html;

            // Move all elements to the target container
            while (temp.firstChild) {
                const elem = temp.firstChild;

                // If it's a script tag, we need to recreate it to execute
                if (elem.tagName === 'SCRIPT') {
                    const script = document.createElement('script');

                    // Copy attributes
                    for (let i = 0; i < elem.attributes.length; i++) {
                        script.setAttribute(elem.attributes[i].name, elem.attributes[i].value);
                    }

                    // Copy content
                    script.textContent = elem.textContent;

                    container.appendChild(script);
                    temp.removeChild(elem);
                } else {
                    container.appendChild(elem);
                }
            }
        },

        // Fill the existing hidden field in the embedded form
        fillHiddenField: function (result) {
            const container = document.getElementById('signupFormContainer');

            // Look for the hidden field by name
            const hiddenField = container.querySelector('input[name="' + this.config.hiddenFieldName + '"]');

            if (hiddenField) {
                hiddenField.value = result;
                console.log('Quiz result set:', result);
            } else {
                console.log('Cannot find hidden field in signup form');
                /* // If not found yet, try finding within any forms
                const forms = container.getElementsByTagName('form');
                if (forms.length > 0) {
                    const form = forms[0];
                    const fieldInForm = form.querySelector('input[name="' + this.config.hiddenFieldName + '"]');
                    if (fieldInForm) {
                        fieldInForm.value = result;
                        console.log('Quiz result set:', result);
                    }
                } */
            }
        },

        // Observe for dynamically injected forms (like Klaviyo)
        observeFormInjection: function (result) {
            const container = document.getElementById('signupFormContainer');
            const self = this;

            const observer = new MutationObserver(function (mutations) {
                mutations.forEach(function (mutation) {
                    if (mutation.addedNodes.length > 0) {
                        self.fillHiddenField(result);
                    }
                });
            });

            observer.observe(container, {
                childList: true,
                subtree: true
            });

            // Stop observing after 10 seconds to prevent memory leaks
            setTimeout(function () {
                observer.disconnect();
            }, 10000);
        }
    };

    // Expose QuizWidget to global scope
    //window.QuizWidget = QuizWidget;
    //const quizContainer = document.getElementById("quiz-container");
    if (document.getElementById("quiz-container")) QuizWidget.init('quiz-container', 'quiz-questions.json');

})(window);